/**
 * DeterrenceHero component.
 *
 * The top band of the /now page. Shows:
 * - Large tabular-mono counter for phones turned away this week
 * - Delta chip: percent change vs previous window (+ cyan, - amber, 0 muted)
 * - Supporting stat strip: Deterrence rate, Coverage uptime, Active now
 *
 * Loading state: skeleton loaders replace all numeric content.
 * Accessibility: role=status aria-live=polite on the container.
 * Animation: Framer Motion scale pulse on counter value change (disabled under
 * prefers-reduced-motion; only functional state changes like color remain).
 *
 * Design spec reference: section 3.1
 */
import { motion, AnimatePresence } from 'framer-motion';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import type { DeterrenceKPIs } from './schemas';
import * as styles from './DeterrenceHero.css';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface Props {
  /** Resolved KPI data; undefined when loading or in error state. */
  kpis: DeterrenceKPIs | undefined;
  /** When true, renders skeleton placeholders instead of values. */
  isLoading: boolean;
  /**
   * Monotonically increasing counter from useStoryCards.
   * Each increment forces a new scale animation cycle on the counter.
   * Passed as key to AnimatePresence so each new value triggers re-animation.
   * Plan 03-06: wired from NowPage; defaults to 0 (no tick) when not provided.
   */
  turnedAwayTick?: number;
  /**
   * Phase 12 Plan 12-11: when provided, the Performance hero becomes
   * clickable and the click handler opens the deterrence breakdown
   * drawer. When omitted, the hero stays read-only (the current
   * pre-Plan-12 behavior on multi-site dashboards where the drawer
   * is not wired). The hero renders as a <button> when this prop is
   * set so keyboard / screen-reader users get a proper activation.
   */
  onPerformanceTileClick?: () => void;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/**
 * Skeleton placeholder for the hero loading state.
 * Renders a pulsing block for the counter and a strip for the stat row.
 */
function HeroSkeleton() {
  return (
    <div className={styles.container} role="status" aria-live="polite" data-testid="hero-skeleton">
      <div className={styles.numberRow}>
        <div className={styles.skeleton} />
      </div>
      <div className={styles.label}>phones turned away this week</div>
      <div className={styles.skeletonStrip} />
    </div>
  );
}

/**
 * Delta chip showing the percent change vs the previous window.
 *
 * @param value - Percent change (e.g. 23.0, -5.0, 0)
 * @param reducedMotion - Whether to skip decorative animations
 */
function Delta({ value }: { value: number }) {
  const prefix = value > 0 ? '+' : '';
  const formatted = `${prefix}${Math.round(value)}%`;

  let className = styles.deltaFlat;
  if (value > 0) className = styles.deltaPositive;
  if (value < 0) className = styles.deltaNegative;

  return <span className={className}>{formatted}</span>;
}

/**
 * A single stat in the supporting strip.
 *
 * @param label - Stat label (e.g. "Deterrence")
 * @param value - Formatted value string (e.g. "94%")
 * @param warn - When true, applies amber color to the value
 * @param testId - Optional data-testid for test assertions
 */
function Stat({
  label,
  value,
  warn,
  testId,
}: {
  label: string;
  value: string;
  warn: boolean;
  testId?: string;
}) {
  return (
    <div className={styles.stat} data-testid={testId} data-warn={warn ? 'true' : 'false'}>
      <span className={warn ? styles.statValueWarn : styles.statValue}>{value}</span>
      <span className={styles.statLabel}>{label}</span>
    </div>
  );
}

/**
 * Animated counter that scale-pulses on value change.
 *
 * Uses a key prop on the motion element so value changes trigger the
 * animation without layout jumps.
 *
 * @param value - Current numeric value to display
 * @param reducedMotion - Skip animation when true
 */
function AnimatedCounter({ value, reducedMotion }: { value: number; reducedMotion: boolean }) {
  if (reducedMotion) {
    return <span className={styles.counter}>{value}</span>;
  }

  return (
    <AnimatePresence mode="wait">
      <motion.span
        key={value}
        className={styles.counter}
        initial={{ scale: 1 }}
        animate={{
          scale: [1, 1.08, 1],
          transition: { duration: 0.12, ease: [0.16, 1, 0.3, 1] },
        }}
      >
        {value}
      </motion.span>
    </AnimatePresence>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

/**
 * DeterrenceHero: the main deterrence KPI display for the /now page.
 *
 * @param kpis - Resolved KPI data or undefined during loading/error
 * @param isLoading - Shows skeleton when true
 * @param turnedAwayTick - Incremented by useStoryCards on each turnaround; triggers counter animation
 */
export function DeterrenceHero({
  kpis,
  isLoading,
  turnedAwayTick = 0,
  onPerformanceTileClick,
}: Props) {
  void turnedAwayTick; // consumed via displayedKpis.turned_away in NowPage; kept as prop for animation key
  const reducedMotion = useReducedMotion();

  if (isLoading || !kpis) {
    return <HeroSkeleton />;
  }

  const deterrencePct = Math.round(kpis.deterrence_rate * 100);
  const coveragePct = (kpis.coverage_uptime_pct * 100).toFixed(1);
  const coverageWarn = kpis.coverage_uptime_pct < 0.95;

  // Phase 12 Plan 12-11: when onPerformanceTileClick is provided the
  // hero wraps in a button so click + keyboard activation both open the
  // drawer. When undefined, the hero stays a passive <div> (legacy
  // read-only behavior). We keep role=status + aria-live on the same
  // element so screen readers still announce KPI updates either way.
  const inner = (
    <>
      <div className={styles.numberRow}>
        <AnimatedCounter value={kpis.turned_away} reducedMotion={reducedMotion} />
        <Delta value={kpis.delta_pct_vs_prev_window} />
      </div>

      <p className={styles.label}>phones turned away this week</p>

      <div className={styles.statStrip}>
        <Stat
          label="Deterrence"
          value={`${deterrencePct}%`}
          warn={false}
          testId="stat-deterrence"
        />
        <div className={styles.divider} aria-hidden="true" />
        <Stat
          label="Coverage"
          value={`${coveragePct}%`}
          warn={coverageWarn}
          testId="stat-coverage"
        />
        <div className={styles.divider} aria-hidden="true" />
        <Stat
          label="Active"
          value={`${kpis.currently_active} active now`}
          warn={false}
          testId="stat-active"
        />
      </div>
    </>
  );

  if (onPerformanceTileClick) {
    // Render the hero as a button while preserving the styled container
    // visuals. type=button avoids accidental form submission if a future
    // refactor wraps the hero in a form. all:unset preserves the existing
    // .container styles via inline override (vanilla-extract style props
    // win over reset).
    return (
      <button
        type="button"
        onClick={onPerformanceTileClick}
        className={styles.container}
        role="status"
        aria-live="polite"
        aria-label="View deterrence event breakdown"
        data-testid="performance-tile-button"
        style={{
          all: 'unset',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '16px',
          padding: '40px 24px 32px',
          textAlign: 'center',
          cursor: 'pointer',
          width: '100%',
        }}
      >
        {inner}
      </button>
    );
  }

  return (
    <div className={styles.container} role="status" aria-live="polite">
      {inner}
    </div>
  );
}
